import { Controller } from "@hotwired/stimulus";

/**
 * Venue Layout Tiers Controller (GR-3146)
 *
 * The venue form renders the org's layout inventory twice, as a Selectable
 * group and a By Request group, plus a Default select over the whole
 * inventory. Three server-side validations reject the invalid combinations,
 * but nothing stopped an admin reaching them, and a rejected save is easy to
 * miss. This keeps the invalid states unreachable in the first place:
 *
 * - a layout ticked in one tier has its twin in the other tier disabled
 * - the Default select only offers layouts the venue actually offers freely,
 *   which is the ticked selectable set, or the whole inventory minus the
 *   by-request ones when no selectable tier is set (the "unconfigured"
 *   case the model allows)
 *
 * Runs on connect too, so a form re-rendered after a failed save comes back
 * in a consistent state rather than preserving the rejected combination.
 */
export default class extends Controller {
  connect() {
    this.selectableBoxes = this.boxesFor("selectable_layout_ids");
    this.byRequestBoxes = this.boxesFor("by_request_layout_ids");
    this.defaultSelect = this.element.querySelector(
      'select[name="venue[default_layout_id]"]'
    );

    if (!this.selectableBoxes.length && !this.byRequestBoxes.length) return;

    this.boundSync = this.sync.bind(this);
    this.allBoxes().forEach((box) =>
      box.addEventListener("change", this.boundSync)
    );

    this.sync();
  }

  disconnect() {
    if (!this.boundSync) return;
    this.allBoxes().forEach((box) =>
      box.removeEventListener("change", this.boundSync)
    );
  }

  sync() {
    this.syncTierExclusivity();
    this.syncDefaultOptions();
  }

  // A layout belongs to at most one tier. Disabling rather than hiding the
  // twin keeps the inventory list stable in both groups, so the admin can
  // see which layouts are spoken for and where.
  syncTierExclusivity() {
    this.pairs().forEach(([selectable, byRequest]) => {
      this.setDisabled(byRequest, selectable.checked);
      this.setDisabled(selectable, byRequest.checked);
    });
  }

  // Mirrors Venue#layout_config_within_org_inventory: the default must be
  // one of the selectable layouts, and with no selectable tier ticked that
  // is the whole active inventory except the by-request ones.
  syncDefaultOptions() {
    if (!this.defaultSelect) return;

    const selectable = this.checkedValues(this.selectableBoxes);
    const byRequest = this.checkedValues(this.byRequestBoxes);
    const allowed = selectable.length
      ? selectable
      : this.values(this.selectableBoxes).filter(
          (value) => !byRequest.includes(value)
        );

    Array.from(this.defaultSelect.options).forEach((option) => {
      // The blank "None" option is always valid.
      option.disabled = option.value !== "" && !allowed.includes(option.value);
    });

    // A default that just became invalid cannot be saved, so drop it rather
    // than leaving a selected-but-disabled option in the control.
    if (this.defaultSelect.selectedOptions[0]?.disabled) {
      this.defaultSelect.value = "";
    }
  }

  setDisabled(box, disabled) {
    box.disabled = disabled;
    // Primer renders the label as a sibling of the input; dimming it is what
    // makes the disabled state legible, the input alone is too subtle.
    box.closest("label, .FormControl-checkbox-wrap")?.classList.toggle(
      "color-fg-muted",
      disabled
    );
  }

  boxesFor(field) {
    return Array.from(
      this.element.querySelectorAll(`input[name="venue[${field}][]"]`)
    );
  }

  allBoxes() {
    return [...this.selectableBoxes, ...this.byRequestBoxes];
  }

  // Same inventory rendered in both groups, so pair them by value rather
  // than by position.
  pairs() {
    return this.selectableBoxes
      .map((selectable) => [
        selectable,
        this.byRequestBoxes.find((box) => box.value === selectable.value),
      ])
      .filter(([, byRequest]) => Boolean(byRequest));
  }

  values(boxes) {
    return boxes.map((box) => box.value);
  }

  checkedValues(boxes) {
    return boxes.filter((box) => box.checked).map((box) => box.value);
  }
}
